import { FC, ReactNode, useEffect, useState } from 'react';

import defaultImage from '~shared/assets/defaultImage200.svg';

import { Box, Button, Empty, Image, PencilIcon, Tag, useWindowInnerWidth } from '~shared/ui';
import { useTranslation } from '~shared/lib/i18n/i18n';

import { OrgCoordinatesEditView } from '~features/kindergarten/organizationInfo/coordinates';

import { IAllLanguageItem, IOrgInfo } from '../../model/types';

const ApiUrl = import.meta.env.VITE_API_URL;
export interface OrgInfoViewProps {
  info: IOrgInfo;
  editSlot: ReactNode;
  qrSlot: ReactNode;
}

export const OrgInfoView: FC<OrgInfoViewProps> = ({ info, editSlot, qrSlot }) => {
  const { t } = useTranslation();
  const windowWidth = useWindowInnerWidth();
  const [image, setImage] = useState(defaultImage);

  useEffect(() => {
    setImage(`${ApiUrl}/kindergarten/organization/photo/lg/${info.organization_foto}`);
  }, [info]);

  if (!info) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const hasFlags = info.is_gov_queue || info.is_mixed_group || info.is_short_group;

  const flagTags = hasFlags ? (
    <div className="flex flex-wrap gap-1">
      {info.is_gov_queue && <Tag color="blue">{t('organization:govQueue')}</Tag>}
      {info.is_mixed_group && <Tag color="purple">{t('organization:mixedGroup')}</Tag>}
      {info.is_short_group && <Tag color="orange">{t('organization:shortGroup')}</Tag>}
    </div>
  ) : null;

  if (windowWidth <= 768) {
    return (
      <Box padding={0}>
        <Image
          src={image}
          alt="organizationPhoto"
          className="max-h-[200px] w-full object-cover"
          fallback={defaultImage}
        />
        <div className="flex justify-between gap-5 p-5">
          <div className="grid gap-[30px]">
            <div>
              <h3 className="text-[20px] leading-tight break-words">{info.organization}</h3>
              <p className="text-[12px]"> {t('organization:kindergarten')}</p>
            </div>
            <p className="leading-5">
              {[info.region, info.district, info.street_txt, info.house, info.flat]
                .filter((a) => a !== null && a !== undefined && a !== '')
                .join(', ')}
            </p>
            <p className="leading-5">
              <span className="text-[12px]">{t('organization:workingDay')}</span>
              <br />
              {info.working_day}
            </p>
            <p className="leading-5">
              <span className="text-[12px]">{t('organization:operatingMode')}</span>
              <br />
              {info.operating_mode}
            </p>
            <p className="leading-5">
              <span className="text-[12px]">{t('organization:eduLanguage')}</span>
              <br />
              {info.languages &&
                info.languages.map((item: IAllLanguageItem) => item.name).join(', ')}
            </p>
            <p className="leading-5">
              <span className="text-[12px]">{t('organization:quota')}</span>
              <br />
              {info.kvota} {t('organization:_places')}
            </p>
            <div className="flex justify-between w-full">
              <span className="leading-5 ">
                <span className="text-[12px]">{t('organization:сoordinates')}</span>
                <br />
                {parseFloat(info.x).toFixed(2)}, {parseFloat(info.y).toFixed(2)}
              </span>

              <OrgCoordinatesEditView
                info={info}
                button={
                  <Button className="flex items-center justify-center px-2">
                    <PencilIcon />
                  </Button>
                }
              />
            </div>
            {flagTags}
            <div className="flex"> {qrSlot}</div>
          </div>
          <div>{editSlot}</div>
        </div>
      </Box>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-1 mb-4">
        <h3 className="text-[20px] leading-tight break-words">{info.organization}</h3>
        <p className="text-[12px]"> {t('organization:kindergarten')}</p>
      </div>
      <Box padding={0}>
        <Image
          src={image}
          alt="organizationPhoto"
          className="max-h-[200px] w-full object-cover"
          fallback={defaultImage}
        />
        <div className="flex justify-between gap-5 p-5">
          <div className="grid gap-6">
            <p className="leading-5">
              {[info.region, info.district, info.street_txt, info.house, info.flat]
                .filter((a) => a !== null && a !== undefined && a !== '')
                .join(', ')}
            </p>
            <p className="leading-5">
              <span className="text-[12px]">{t('organization:ownership')}</span>
              <br />
              {info.ownership}
            </p>
            <p className="leading-5">
              <span className="text-[12px]">{t('organization:operatingMode')}</span>
              <br />
              {info.operating_mode}
            </p>
            <p className="leading-5">
              <span className="text-[12px]">{t('organization:workingDay')}</span>
              <br />
              {info.working_day}
            </p>
            <p className="leading-5">
              <span className="text-[12px]">{t('organization:eduLanguage')}</span>
              <br />
              {info.languages &&
                info.languages.map((item: IAllLanguageItem) => item.name).join(', ')}
            </p>
            <p className="leading-5">
              <span className="text-[12px]">{t('organization:quota')}</span>
              <br />
              {info.kvota} {t('organization:_places')}
            </p>
            <div className="flex justify-between w-full items-center">
              <span className="leading-5 ">
                <span className="text-[12px]">{t('organization:сoordinates')}</span>
                <br />
                {parseFloat(info.x).toFixed(2)}, {parseFloat(info.y).toFixed(2)}
              </span>

              <OrgCoordinatesEditView
                info={info}
                button={
                  <Button className="flex items-center justify-center px-2">
                    <PencilIcon />
                  </Button>
                }
              />
            </div>
            {flagTags}
            <div className="flex"> {qrSlot}</div>
          </div>
          <div>{editSlot}</div>
        </div>
      </Box>
    </>
  );
};
